import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { fetchKeywords } from 'core/store/keywords/actions';

interface KeywordsContextType {
  keywords: string[];
  loading: boolean;
  refetch: () => void;
}

const KeywordsContext = createContext<KeywordsContextType>({
  keywords: [],
  loading: false,
  refetch: () => {
    console.log('referetch keywords');
  },
});

/**
 * Provider that fetches keywords once per form
 * Wrap each form with this provider to get fresh keywords per form
 *
 * Example:
 * <KeywordsFormProvider>
 *   <Input withKeywords />
 *   <Input withKeywords />
 * </KeywordsFormProvider>
 */
export const KeywordsFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const [keywords, setKeywords] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(() => {
    setLoading(true);

    dispatch(
      fetchKeywords((result) => {
        if (!isMountedRef.current) return;

        // Fetch when provider mounts
        setLoading(false);
        if (!result.cancelled && !result.error && result.data?.length > 0) {
          setKeywords(result.data);
        }
      })
    );
  }, [dispatch]);

  useEffect(() => {
    fetchData();

    // Cleanup: mark as unmounted to prevent state updates
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData]);

  return (
    <KeywordsContext.Provider value={{ keywords, loading, refetch: fetchData }}>{children}</KeywordsContext.Provider>
  );
};

export const useFormKeywords = () => useContext(KeywordsContext);
